import { useGetMotionTypes } from "@/hooks/queryHooks/configHooks";
import { cn } from "@repo/shared/utils";
import type { MotionType } from "@repo/types";
import { Button } from "@repo/ui/components/Base/Button";
import { Input } from "@repo/ui/components/Base/Input";
import {
  Popover,
  PopoverContent,
  PopoverTrigger,
} from "@repo/ui/components/Base/Popover";
import { Spinner } from "@repo/ui/components/Base/Spinner";
import { Tooltip } from "@repo/ui/components/Base/Tooltip";
import Fuse from "fuse.js";
import { HandIcon, MessageCircleIcon } from "lucide-react";
import { useMemo, useState } from "react";

export interface MotionListProps {
  motionFor: "meeting" | "item";
  onPickMotion?: MotionPanelProps["onPickMotion"];
}

export const MotionList = ({ motionFor, onPickMotion }: MotionListProps) => {
  const [searchQuery, setSearchQuery] = useState("");

  const { data: motionTypes } = useGetMotionTypes();

  const availableMotions = useMemo(
    () => motionTypes?.[motionFor] ?? [],
    [motionFor, motionTypes]
  );

  const fuse = useMemo(
    () =>
      new Fuse(availableMotions, {
        keys: ["name", "nicknames"],
        findAllMatches: true,
      }),
    [availableMotions]
  );

  const search = (query: string) =>
    query ? fuse.search(query).map(({ item }) => item) : availableMotions;

  return (
    <div className="w-96 max-h-80 flex">
      <div className="flex flex-col gap-y-2 flex-grow">
        <Input
          id="search"
          placeholder="Search..."
          className="bg-transparent h-8 text-white -mt-1"
          value={searchQuery}
          onChange={(evt) => setSearchQuery(evt.target.value)}
        />
        <div className="overflow-y-auto flex-grow pr-2">
          {motionTypes ? (
            search(searchQuery).map((motion) => (
              <Tooltip
                disableHoverableContent
                key={motionFor + ":" + motion.id}
                label={motion.description}
                className="shadow-lg pointer-events-none"
                asChild
              >
                <Button
                  type="button"
                  onClick={() => onPickMotion?.(motion)}
                  className={
                    "flex flex-row w-full" +
                    " relative z-0 overflow-hidden hover:after:absolute hover:after:inset-0 hover:after:bg-white/10 active:after:bg-black/10 after:-z-10"
                  }
                >
                  <span className="flex-grow text-left">{motion.name}</span>
                </Button>
              </Tooltip>
            ))
          ) : (
            <div className="flex-grow flex flex-row justify-center items-center">
              <Spinner />
            </div>
          )}
        </div>
      </div>
    </div>
  );
};

export interface MotionPanelProps {
  label?: React.ReactNode;
  corner?: "topleft" | "bottomleft";
  motionFor: "meeting" | "item";
  onPickMotion?: (motion: MotionType) => void;
  className?: string;
}

export const MotionPanel = ({
  label = "Motion",
  corner = "bottomleft",
  motionFor,
  onPickMotion,
  className,
}: MotionPanelProps) => {
  const [open, setOpen] = useState(false);

  return (
    <Popover open={open} onOpenChange={setOpen}>
      <PopoverTrigger asChild>
        <Button
          type="button"
          className={cn(
            "flex flex-col w-16 h-16 rounded-full text-xs",
            { topleft: "rounded-tl-none", bottomleft: "rounded-bl-none" }[
              corner
            ],
            className
          )}
        >
          <span className="flex flex-row">
            <HandIcon size={20} />
            <MessageCircleIcon size={14} />
          </span>
          {label}
        </Button>
      </PopoverTrigger>
      <PopoverContent
        className="w-full h-full bg-primary border-primary p-2 rounded-xl"
        side="right"
        align="end"
        alignOffset={0}
        sideOffset={10}
      >
        <MotionList
          motionFor={motionFor}
          onPickMotion={(motion) => {
            setOpen(false);
            onPickMotion?.(motion);
          }}
        />
      </PopoverContent>
    </Popover>
  );
};
